import React, { useState } from 'react'
import { checkIsShippingValid } from '../../shipping/utils'
import { SocialShareSelection } from './SocialShareSelectionType'
import { CartSuggestion } from './CartSuggestion'
import { SharePayment } from './SharePayment'
import {
  CountryCode,
  LocalizationConfig,
  LocalizationProvider,
  useLocalization,
  useLocalizationState,
} from '@vtexlab/assisted-sales-localization'

type SocialShareContainerProps = {
  orderForm: OrderForm
  shareCode: string
  type?: 'selection' | 'cart' | 'payment'
  isCustomerAnonymousNameSelector: boolean
}

type SocialShareProps = SocialShareContainerProps

const SocialShareContainer = ({
  orderForm,
  shareCode,
  isCustomerAnonymousNameSelector,
  type,
}: SocialShareContainerProps) => {
  const { localizationConfig } = useLocalization()

  // Based on https://github.com/vtex/delivery-packages/blob/master/src/address.js#L67
  const requiredAddressFields = [
    'state',
    'city',
    'neighborhood',
    'street',
    'number',
  ].filter(
    (field) =>
      localizationConfig.input[field as keyof LocalizationConfig['input']]
        .enabled
  )

  const getDefaultType = () => {
    if (type) {
      return type
    }

    const isShippingValid = checkIsShippingValid(
      orderForm.shippingData,
      false,
      requiredAddressFields
    )

    return isShippingValid && !isCustomerAnonymousNameSelector
      ? 'selection'
      : 'cart'
  }

  const [shareType, setShareType] = useState(getDefaultType())

  const vendorCode = orderForm.openTextField ? orderForm.openTextField : ''

  switch (shareType) {
    case 'selection':
      return (
        <SocialShareSelection
          shareCart={() => {
            setShareType('cart')
          }}
          sharePayment={() => {
            setShareType('payment')
          }}
        />
      )

    case 'cart':
      return <CartSuggestion shareCode={shareCode} />

    case 'payment':
      return <SharePayment vendorCode={vendorCode} shareCode={shareCode} />
  }
}

const SocialShare = (props: SocialShareProps) => {
  const countryCode = props.orderForm?.storePreferencesData
    ?.countryCode as CountryCode

  const localizationState = useLocalizationState({
    countryCode,
  })

  return (
    <LocalizationProvider value={localizationState}>
      <SocialShareContainer {...props} />
    </LocalizationProvider>
  )
}

export default SocialShare
